import {DataGrid} from "@mui/x-data-grid";
import './DataGridWrapper.scss'
import React, {useContext, useEffect, useRef, useState} from "react";
import LoadingSpinner from "../../Components/LoadingSpinner/LoadingSpinner";
import {SearchFilter} from "../GridFilters/SearchFilter/SerachFilter";
import {OrganizationFilter} from "../GridFilters/OrganizationFilter/OrganizationFilter";
import {GridReactContext} from "./GridContext";
import useDebounce from "../../Hooks/useDebounce";
import {CompanyFilter} from "../GridFilters/CompanyFilter/CompanyFilter";
import {get, isEqual, map} from "lodash";
import {getSignDocOptions} from "../../Components/Reports/SignDocsStats/SignDocsStatsLib";
import {Button} from "../../Components/Form/Button/Button";
import {buttonColors} from "../../Components/Pickers/InputTheme";
import {SignDocFilter} from "../GridFilters/SignDocFilter/SignDocFilter";
import {FileTypeFilter} from "../GridFilters/FileTypeFilter/FileTypeFilter";
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import {MenuListComposition} from "../../Components/Form/ButtonMenu/ButtonMenu";
import {I18nReactContext} from "../../contexts/I18nContext";
import intersectionIcon from "../../assets/images/intersection-icon.png";
import {SignDocCategories} from "../Constants";

const defaultPaginationModel = {
    page: 0,
    pageSize: 5,
};
const defaultFilterModel = {}
const defaultSortModel = []

/**
 * DataGridWrapper component that provides a customizable wrapper around MUI DataGrid with filtering, pagination, and synchronized dual horizontal scrollbars.
 *
 * @param {object} params - The function parameters container object.
 * @param {function} params.getColumns - Function to get column definitions for the grid.
 * @param {object} params.paginationModel - Pagination configuration object.
 * @param {function} params.apiToGetData - API function to fetch data for the grid.
 * @param {function} params.dataConverter - Function to convert API response data to grid format.
 * @param {boolean} params.disableColumnFilter - Whether to disable built-in column filtering.
 * @param {object} params.filterModel - Filter configuration object.
 * @param {object} params.sortModel - Sort configuration object.
 * @param {Array} params.buttons - Array of button configurations for the grid toolbar.
 * @param {boolean} params.checkboxSelection - Whether to enable row checkbox selection.
 * @param {function} params.onSelectionModelChange - Callback for selection changes.
 * @param {Array} params.selectionModel - Currently selected row IDs.
 * @param {function} params.isRowSelectable - Function to determine if a row is selectable.
 * @param {function} [params.rowsTransformer] - Transforms the fetched rows right before they are rendered.
 *                                              Lets a parent add or remove rows (for example expanding a row
 *                                              into sub-rows) without re-fetching. Defaults to no change.
 * @param {function} [params.getRowClassName] - MUI DataGrid callback returning a CSS class for a row.
 *                                              Used to style specific rows, e.g. expanded sub-rows.
 * @returns {JSX.Element} The DataGridWrapper component with top and bottom synchronized scrollbars.
 */
export const DataGridWrapper = ({
                                    getColumns,
                                    paginationModel,
                                    apiToGetData,
                                    dataConverter,
                                    disableColumnFilter,
                                    filterModel,
                                    sortModel,
                                    buttons,
                                    checkboxSelection,
                                    onSelectionModelChange,
                                    selectionModel,
                                    isRowSelectable,
                                    adminOrganizations, // Direct prop for SignDoc filtering, NOT sent to backend API
                                    rowsTransformer,
                                    getRowClassName
                                }) => {
    const [innerPaginationModel, setInnerPaginationModel] = useState(paginationModel || defaultPaginationModel);
    const [innerFilterModel, setInnerFilterModel] = useState(filterModel || defaultFilterModel);
    const [innerSortModel, setInnerSortModel] = useState(sortModel || defaultSortModel)
    const [innerRowSelectionModel, setInnerRowSelectionModel] = useState(selectionModel || [])
    const [data, setData] = useState([])
    const [count, setCount] = useState(0)
    const [columns, setColumns] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const debouncedInnerFilterModel = useDebounce({value: innerFilterModel, delay: 500})
    const debouncedInnerSortModel = useDebounce({value: innerSortModel, delay: 500})
    const preventOnceFilter = useRef(true)
    const preventOnceSort = useRef(true)
    const preventOnceRefreshKey = useRef(true)
    const gridContext = useContext(GridReactContext)
    const {i18n} = useContext(I18nReactContext);
    
    // Popover state for intersection help
    const [intersectionAnchorEl, setIntersectionAnchorEl] = useState(null);
    
    const handleIntersectionPopoverOpen = (event) => {
        setIntersectionAnchorEl(event.currentTarget);
    };

    const handleIntersectionPopoverClose = () => {
        setIntersectionAnchorEl(null);
    };

    const intersectionPopoverOpen = Boolean(intersectionAnchorEl);
    
    // Refs for horizontal scrollbar synchronization
    const topScrollerRef = useRef(null);
    const dataGridRef = useRef(null);
    const isTopScrolling = useRef(false);
    const isBottomScrolling = useRef(false);
    
    /**
     * Sync external selectionModel prop with internal state
     * This ensures selections persist across page changes
     */
    useEffect(() => {
        if (selectionModel !== undefined) {
            setInnerRowSelectionModel(selectionModel);
        }
    }, [selectionModel]);

    /**
     * Sync external filterModel.filterData prop with internal state
     * Only syncs filterData (like organizations list) when it changes from parent
     * Does NOT sync filterValues to preserve user's filter selections
     */
    useEffect(() => {
        if (filterModel?.filterData) {
            setInnerFilterModel(prevFilter => {
                // Only update if filterData actually changed
                if (isEqual(filterModel.filterData, prevFilter.filterData)) {
                    return prevFilter;
                }
                // Merge new filterData from prop while preserving user's filterValues
                return {
                    ...prevFilter,
                    filterData: {
                        ...prevFilter.filterData,
                        ...filterModel.filterData
                    }
                };
            });
        }
    }, [filterModel?.filterData]);

    /**
     * Synchronizes scrolling between top scroller and DataGrid bottom scrollbar.
     * Sets up bidirectional scroll event listeners to keep both scrollbars in sync.
     */
    useEffect(() => {
        const topScroller = topScrollerRef.current;
        const dataGridElement = dataGridRef.current;
        
        if (!topScroller || !dataGridElement) return;

        // Find the actual scrollable element within the DataGrid
        const dataGridScrollable = dataGridElement.querySelector('.MuiDataGrid-virtualScroller');
        
        if (!dataGridScrollable) return;

        const handleTopScroll = () => {
            if (isBottomScrolling.current) return;
            isTopScrolling.current = true;
            dataGridScrollable.scrollLeft = topScroller.scrollLeft;
            requestAnimationFrame(() => {
                isTopScrolling.current = false;
            });
        };

        const handleBottomScroll = () => {
            if (isTopScrolling.current) return;
            isBottomScrolling.current = true;
            topScroller.scrollLeft = dataGridScrollable.scrollLeft;
            requestAnimationFrame(() => {
                isBottomScrolling.current = false;
            });
        };

        // Add event listeners
        topScroller.addEventListener('scroll', handleTopScroll);
        dataGridScrollable.addEventListener('scroll', handleBottomScroll);

        const updateScrollerWidth = () => {
            const dataGridContent = dataGridElement.querySelector('.MuiDataGrid-virtualScrollerContent');
            if (dataGridContent && topScroller.firstChild) {
                topScroller.firstChild.style.width = `${dataGridContent.scrollWidth}px`;
                topScroller.firstChild.style.height = `${dataGridContent.scrollHeight}px`;
            }
        };

        updateScrollerWidth();


        // Cleanup
        return () => {
            topScroller.removeEventListener('scroll', handleTopScroll);
            dataGridScrollable.removeEventListener('scroll', handleBottomScroll);
        };
    }, [data, columns]); 

    useEffect(() => {
        if (preventOnceFilter.current) {
            preventOnceFilter.current = false
            return
        }
        fetchServerData({
            paginationModel: innerPaginationModel,
            filterModel: debouncedInnerFilterModel,
            sortModel: innerSortModel
        })
    }, [debouncedInnerFilterModel])
    useEffect(() => {
        if (preventOnceSort.current) {
            preventOnceSort.current = false
            return
        }
        fetchServerData({
            paginationModel: innerPaginationModel,
            filterModel: innerFilterModel,
            sortModel: debouncedInnerSortModel
        })
    }, []);


    useEffect(() => {
        if (preventOnceRefreshKey.current) {
            preventOnceRefreshKey.current = false
            return
        }
        fetchServerData({
            paginationModel: innerPaginationModel,
            filterModel: innerFilterModel,
            sortModel: innerSortModel
        })
    }, [gridContext.refreshKey]);

    useEffect(() => {
        if(!isEqual(innerRowSelectionModel,gridContext.selectionModel)){
            setInnerRowSelectionModel(gridContext.selectionModel);
        }
    }, [gridContext.selectionModel]);


    const fetchServerData = async ({paginationModel, filterModel, sortModel}) => {
        setIsLoading(true)
        const res = await apiToGetData({paginationModel, filterModel, sortModel})
        const convertedData = dataConverter(get(res, 'data', {}))
        setCount(get(res, 'count', 0))
        setData(convertedData)
        setIsLoading(false)
    }

    useEffect(() => {
        const newPaginationModel = {
            ...innerPaginationModel
        }
        newPaginationModel.page = 0
        setInnerPaginationModel(newPaginationModel)
        getColumns({paginationModel: newPaginationModel, filterModel: innerFilterModel, i18n})
            .then((res) => {
                // Only update columns if they've actually changed
                setColumns(prevColumns => {
                    if (isEqual(prevColumns, res)) {
                        return prevColumns;
                    }
                    return res;
                });
            })
        gridContext.updateFilterModel(innerFilterModel)
    }, [innerFilterModel, i18n]);

    /**
     * Effect to handle signDoc options when organization filters change
     *
     * SignDoc filtering logic based on organization selection:
     * 1. Main = 'all', Intersection = 'all'        → Show all SignDocs
     * 2. Main = 'all', Intersection = [A, B]       → Show SignDocs from A ∪ B only
     * 3. Main = [A, B], Intersection = 'all'       → Show SignDocs from A ∪ B
     * 4. Main = [A, B], Intersection = [C, D]      → Show SignDocs from A ∪ B ∪ C ∪ D (union of both)
     */
    const lastOrgFilterStateRef = useRef(null);

    useEffect(() => {
        const organizationIds = get(innerFilterModel, 'filterValues.organizationIds', []);
        const organizationIntersectionIds = get(innerFilterModel, 'filterValues.organizationIntersectionIds', null);

        // Create a state key to detect actual changes
        const currentFilterState = JSON.stringify({ organizationIds, organizationIntersectionIds });

        // Only proceed if the filter state actually changed
        if (currentFilterState !== lastOrgFilterStateRef.current) {
            lastOrgFilterStateRef.current = currentFilterState;
            try {
                // Determine which organizations to use for SignDoc filtering
                const isIntersectionActive = organizationIntersectionIds && !organizationIntersectionIds.includes('all') && organizationIntersectionIds.length > 0;
                const isMainOrgAll = organizationIds.includes('all');

                let selectedOrgOptions;

                if (isIntersectionActive) {
                    if (isMainOrgAll) {
                        // Main org is 'all' but intersection has specific orgs → use only intersection orgs
                        selectedOrgOptions = organizationIntersectionIds;
                    } else {
                        // Both filters have specific orgs → use union of both
                        selectedOrgOptions = [...new Set([...organizationIds, ...organizationIntersectionIds])];
                    }
                } else {
                    // Intersection is 'all' → use only main organizations
                    selectedOrgOptions = organizationIds;
                }

                // Get signDoc options based on computed organization selection
                const signDocTypes = get(filterModel, 'filterData.signDocTypes', null);
                // Get categories from filterData, or default to FORCE_COMPLETION and CERTIFICATION
                const categories = get(filterModel, 'filterData.categories', [SignDocCategories.FORCE_COMPLETION, SignDocCategories.CERTIFICATION]);

                const { signDocOptionsTmp } = getSignDocOptions({
                    selectedOrgOptions,
                    adminOrganizations: adminOrganizations || [],
                    categories: categories,
                    i18n,
                    types: signDocTypes
                });

                // Update filterData with new signDoc options
                setInnerFilterModel(prevFilter => ({
                    ...prevFilter,
                    filterData: {
                        ...prevFilter.filterData,
                        signDocs: signDocOptionsTmp
                    }
                }));
            } catch (error) {
                console.error('Error updating signDoc options:', error);
            }
        }
    }, [innerFilterModel.filterValues.organizationIds, innerFilterModel.filterValues.organizationIntersectionIds, i18n]);

    useEffect(() => {
        if(!isEqual(innerRowSelectionModel, gridContext.selectionModel)){
            gridContext.updateSelectionModel(innerRowSelectionModel)
        }
    }, [innerRowSelectionModel]);

    useEffect(() => {
        gridContext.updateSortModel(innerSortModel)
    }, [innerSortModel]);

    useEffect(() => {
        gridContext.updatePaginationModel(innerPaginationModel)
    }, [innerPaginationModel]);

    const handlePaginationModelChange = (newPaginationModel) => {
        setInnerPaginationModel(newPaginationModel);
        fetchServerData({paginationModel: newPaginationModel, filterModel: innerFilterModel, sortModel: innerSortModel})
    }
    const handleSortModelChange = (newSortModel) => {
        setInnerSortModel(newSortModel);
        const newPaginationModel = {
            ...innerPaginationModel
        }
        newPaginationModel.page = 0
        setInnerPaginationModel(newPaginationModel)
        fetchServerData({paginationModel: newPaginationModel, filterModel: innerFilterModel, sortModel: newSortModel})
    }

    const handleSelectionModelChange = (newSelectionModel) => {
        setInnerRowSelectionModel(newSelectionModel);
        // Update selectedRows with full row objects for selected IDs
        const newSelectedRows = data.filter(row => newSelectionModel.includes(row.id))
        gridContext.updateSelectedRows(newSelectedRows)
        onSelectionModelChange({newSelectionModel})
    }

    /**
     * Creates the filter model for the intersection organization filter
     * @returns {object} Filter model configured for intersection filter
     */
    const getIntersectionFilterModel = () => ({
        ...innerFilterModel,
        filterData: {
            ...innerFilterModel.filterData,
            organizations: get(innerFilterModel, 'filterData.organizationIntersection', [])
        },
        filterValues: {
            ...innerFilterModel.filterValues,
            organizationIds: get(innerFilterModel, 'filterValues.organizationIntersectionIds', ['all'])
        }
    });

    /**
     * Handles filter model changes for the intersection organization filter
     * Supports both direct object and functional update patterns (like React setState)
     * @param {object|function} newFilterModelOrUpdater - The new filter model or updater function from OrganizationFilter
     */
    const handleIntersectionFilterModelChange = (newFilterModelOrUpdater) => {
        const resolvedFilterModel = typeof newFilterModelOrUpdater === 'function'
            ? newFilterModelOrUpdater(getIntersectionFilterModel())
            : newFilterModelOrUpdater;
        const organizationIntersectionIds = get(resolvedFilterModel, 'filterValues.organizationIds', ['all']);
        const currentIntersectionIds = get(innerFilterModel, 'filterValues.organizationIntersectionIds', ['all']);
        
        // Only update if the values actually changed to prevent unnecessary re-renders
        if (!isEqual(organizationIntersectionIds, currentIntersectionIds)) {
            setInnerFilterModel({
                ...innerFilterModel,
                filterValues: {
                    ...innerFilterModel.filterValues,
                    organizationIntersectionIds
                }
            });
        }
    };

    /**
     * Renders the intersection icon with tooltip popover
     * @returns {JSX.Element} The intersection icon container with popover tooltip
     */
    const renderIntersectionIcon = () => (
        <div className="intersection-icon-container filter">
            <Typography
                aria-owns={intersectionPopoverOpen ? 'intersection-mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handleIntersectionPopoverOpen}
                onMouseLeave={handleIntersectionPopoverClose}
            >
                <img 
                    src={intersectionIcon} 
                    alt="Intersection" 
                    className="intersection-icon"
                />
            </Typography>
            <Popover
                id="intersection-mouse-over-popover"
                sx={{ pointerEvents: 'none' }}
                open={intersectionPopoverOpen}
                anchorEl={intersectionAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handleIntersectionPopoverClose}
                disableRestoreFocus
            >
                <Typography sx={{ p: 2, maxWidth: 400 }}>
                    {i18n.translate('organizationIntersectionFilter.tooltip')}
                </Typography>
            </Popover>
        </div>
    );

    // Rows actually rendered. A parent can inject or hide rows here without triggering a re-fetch.
    const rows = rowsTransformer ? rowsTransformer(data) : data

    return <div className="DataGrid">
        <div className="filterContainer">
            {filterModel?.showFilters?.search && <div className="searchFilterContainer filter">
                <SearchFilter filterModel={innerFilterModel} setFilterModel={setInnerFilterModel}/>
            </div>}
            {filterModel?.showFilters?.organizations && <div className="organizationFilterContainer filter">
                <OrganizationFilter 
                    filterModel={innerFilterModel} 
                    setFilterModel={setInnerFilterModel}
                    enableMultiSelect={true}
                    enableSelectAll={true}
                />
            </div>}
            {filterModel?.showFilters?.organizationIntersection && (
                <>
                    {renderIntersectionIcon()}
                    <div className="organizationFilterContainer filter">
                        <OrganizationFilter 
                            filterModel={getIntersectionFilterModel()}
                            setFilterModel={handleIntersectionFilterModelChange}
                            enableMultiSelect={true}
                            enableSelectAll={true}
                        />
                    </div>
                </>
            )}
            {filterModel?.showFilters?.companies && <div className="organizationFilterContainer filter">
                <CompanyFilter 
                    filterModel={innerFilterModel} 
                    setFilterModel={setInnerFilterModel}
                    enableMultiSelect={true}
                />
            </div>}
            {filterModel?.showFilters?.signDocs && <div className="organizationFilterContainer filter taskFilterContainer">
                <SignDocFilter filterModel={innerFilterModel} setFilterModel={setInnerFilterModel}
                              disabledObj={get(gridContext, 'filterDisabled.signDocs')}/>
            </div>}
            {filterModel?.showFilters?.fileType && <div className="organizationFilterContainer filter">
                <FileTypeFilter filterModel={innerFilterModel} setFilterModel={setInnerFilterModel}
                              disabledObj={get(gridContext, 'filterDisabled.fileType')}/>
            </div>}
            {map(buttons, (button, index) => {
                const innerOnClick = async () => {
                    await button.onClick({
                        paginationModel: innerPaginationModel,
                        filterModel: innerFilterModel,
                        sortModel: innerSortModel,
                        selectionModel: innerRowSelectionModel
                    })
                }
                const buttonKey = button.key || button.text || `button-${button.text}-${index}`;

                if (button.type === "IconButton") {
                    return <IconButton key={buttonKey} title={button.label} onClick={innerOnClick} size="large">
                        {button.icon}
                    </IconButton>

                }
                if(button.type === "A-Button") {
                    return <a key={buttonKey} className="a-button"
                              href={button.href}
                              target={button.target}>
                        <div className="iconWrapper">{button.icon}</div>
                        <div>{button.text}</div>
                    </a>
                }
                if (button.type === "MultipleOptionsButton") {
                    //wrap option actions
                    const optionActionWrapper = (action) => {
                        return async () => {
                            if(action && typeof action === "function"){
                                await action({
                                    paginationModel: innerPaginationModel,
                                    filterModel: innerFilterModel,
                                    sortModel: innerSortModel,
                                    selectionModel: innerRowSelectionModel
                                })
                            }
                        }
                    }
                    //go over options and convert the actions and evaluate disabled/shouldShow
                    const options = map(button.options, (option) => {
                        // Evaluate disabled if it's a function
                        let disabled = option.disabled;
                        if (typeof disabled === 'function') {
                            disabled = disabled({
                                paginationModel: innerPaginationModel,
                                filterModel: innerFilterModel,
                                sortModel: innerSortModel,
                                selectionModel: innerRowSelectionModel
                            });
                        }

                        // Evaluate shouldShow if it's a function
                        let shouldShow = option.shouldShow;
                        if (typeof shouldShow === 'function') {
                            shouldShow = shouldShow({
                                paginationModel: innerPaginationModel,
                                filterModel: innerFilterModel,
                                sortModel: innerSortModel,
                                selectionModel: innerRowSelectionModel
                            });
                        }

                        // If shouldShow is false, skip this option
                        if (shouldShow === false) {
                            return null;
                        }

                        return {
                            ...option,
                            action: optionActionWrapper(option.action),
                            disabled
                        }
                    }).filter(Boolean) // Remove null entries from shouldShow: false
                    
                    return <MenuListComposition
                        key={buttonKey}
                        text={button.text}
                        icon={button.icon}
                        loading={button.loading}
                        disabled={button.disabled}
                        options={options}/>
                }
                if (button.type === "ComponentButton") {
                    const Component = button.component
                    return <Component key={buttonKey}/>
                }
                return <Button
                    key={buttonKey}
                    icon={button.icon}
                    color={button.color || buttonColors.GREY}
                    onClick={innerOnClick}
                    text={button.text}
                    loading={button.loading}
                    disabled={button.disabled}
                />
            })}
        </div>
        <div className="tableContainer">
            <div className="topScroller" ref={topScrollerRef}>
                <div className="topScrollerContent"></div>
            </div>
            
            {isLoading && <LoadingSpinner/>}
            <div ref={dataGridRef} style={{flex: 1, minHeight: 0}}>
                <DataGrid
                    sx={{height: '100%'}}
                    // MUI ships its own English strings. This is the only DataGrid in the app,
                    // so translating here covers every table.
                    localeText={{
                        footerRowSelected: (count) => i18n.translate('dataGrid.footer.rowsSelected', {context: {count}})
                    }}
                    sortingMode="server"
                    disableColumnFilter={disableColumnFilter}
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    getRowClassName={getRowClassName}
                    paginationModel={innerPaginationModel}
                    rowCount={count}
                    disableRowSelectionOnClick
                    paginationMode="server"
                    pageSizeOptions={[]}
                    onPaginationModelChange={handlePaginationModelChange}
                    onSortModelChange={handleSortModelChange}
                    checkboxSelection={checkboxSelection}
                    onRowSelectionModelChange={handleSelectionModelChange}
                    rowSelectionModel={innerRowSelectionModel}
                    keepNonExistentRowsSelected
                    isRowSelectable={isRowSelectable}
                    getRowClassName={getRowClassName}
                />
            </div>
        </div>
    </div>
}
